/**
 * Security utilities for PIN-based app lock
 * Provides client-side validation and secure storage helpers
 */

/**
 * Validate PIN format (6 digits)
 */
export function validatePinFormat(pin: string): { valid: boolean; error?: string } {
  if (!pin || pin.length === 0) {
    return { valid: false, error: 'PIN is required' };
  }

  if (pin.length !== 6) {
    return { valid: false, error: 'PIN must be exactly 6 digits' };
  }

  if (!/^\d+$/.test(pin)) {
    return { valid: false, error: 'PIN must contain only digits' };
  }

  return { valid: true };
}

/**
 * Check if PIN is too simple (sequential, repeated digits)
 */
export function checkPinStrength(pin: string): { strong: boolean; warning?: string } {
  // Check for repeated digits (1111, 2222, etc.)
  if (/^(\d)\1+$/.test(pin)) {
    return { strong: false, warning: 'PIN should not have all same digits' };
  }

  // Check for sequential ascending (1234, 2345, etc.)
  let isSequential = true;
  for (let i = 0; i < pin.length - 1; i++) {
    if (parseInt(pin[i]) + 1 !== parseInt(pin[i + 1])) {
      isSequential = false;
      break;
    }
  }
  if (isSequential) {
    return { strong: false, warning: 'PIN should not be sequential' };
  }

  // Check for sequential descending (4321, 5432, etc.)
  isSequential = true;
  for (let i = 0; i < pin.length - 1; i++) {
    if (parseInt(pin[i]) - 1 !== parseInt(pin[i + 1])) {
      isSequential = false;
      break;
    }
  }
  if (isSequential) {
    return { strong: false, warning: 'PIN should not be sequential' };
  }

  // Check for common patterns
  const commonPins = ['0000', '1234', '1111', '1212', '7777', '1004'];
  if (commonPins.includes(pin)) {
    return { strong: false, warning: 'PIN is too common. Choose a unique PIN' };
  }

  return { strong: true };
}

/**
 * Lock state storage keys
 */
const STORAGE_KEYS = {
  LOCK_ENABLED: 'app_lock_enabled',
  AUTO_LOCK_TIMEOUT: 'app_lock_timeout',
  LAST_ACTIVITY: 'app_last_activity',
  HAS_PIN: 'app_has_pin',
} as const;

/**
 * Get lock state from localStorage
 */
export function getLockState() {
  try {
    return {
      isEnabled: localStorage.getItem(STORAGE_KEYS.LOCK_ENABLED) === 'true',
      autoLockTimeout: parseInt(localStorage.getItem(STORAGE_KEYS.AUTO_LOCK_TIMEOUT) || '86400', 10),
      lastActivity: parseInt(localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY) || '0', 10),
      hasPin: localStorage.getItem(STORAGE_KEYS.HAS_PIN) === 'true',
    };
  } catch (error) {
    console.error('Failed to get lock state:', error);
    return {
      isEnabled: false,
      autoLockTimeout: 86400,
      lastActivity: 0,
      hasPin: false,
    };
  }
}

/**
 * Save lock state to localStorage
 */
export function saveLockState(state: {
  isEnabled?: boolean;
  autoLockTimeout?: number;
  lastActivity?: number;
  hasPin?: boolean;
}) {
  try {
    if (state.isEnabled !== undefined) {
      localStorage.setItem(STORAGE_KEYS.LOCK_ENABLED, state.isEnabled.toString());
    }
    if (state.autoLockTimeout !== undefined) {
      localStorage.setItem(STORAGE_KEYS.AUTO_LOCK_TIMEOUT, state.autoLockTimeout.toString());
    }
    if (state.lastActivity !== undefined) {
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, state.lastActivity.toString());
    }
    if (state.hasPin !== undefined) {
      localStorage.setItem(STORAGE_KEYS.HAS_PIN, state.hasPin.toString());
    }
  } catch (error) {
    console.error('Failed to save lock state:', error);
  }
}

/**
 * Clear lock state from localStorage
 */
export function clearLockState() {
  try {
    localStorage.removeItem(STORAGE_KEYS.LOCK_ENABLED);
    localStorage.removeItem(STORAGE_KEYS.AUTO_LOCK_TIMEOUT);
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(STORAGE_KEYS.HAS_PIN);
  } catch (error) {
    console.error('Failed to clear lock state:', error);
  }
}

/**
 * Update last activity timestamp
 */
export function updateLastActivity() {
  saveLockState({ lastActivity: Date.now() });
}

/**
 * Check if app should be locked based on inactivity
 */
export function shouldLockApp(): boolean {
  const state = getLockState();
  
  if (!state.isEnabled || !state.hasPin) {
    return false;
  }

  const now = Date.now();
  const inactiveTime = (now - state.lastActivity) / 1000; // seconds
  
  return inactiveTime >= state.autoLockTimeout;
}

/**
 * Format timeout seconds to human-readable string
 */
export function formatTimeout(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} seconds`;
  }

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }

  const days = Math.floor(seconds / 86400);
  return `${days} day${days > 1 ? 's' : ''}`;
}
